import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  agendaCitaTipoLabel,
  fetchAgendaReceipt,
  formatAgendaPrecio,
  formatSlotLabel,
} from '../services/agendaService';
import { getUserSession } from '../services/authService';
import '../styles/agenda.css';

const numeroRecibo = (valor) => {
  const texto = String(valor || '');

  if (!texto) {
    return '';
  }

  if (texto.length >= 6) {
    return texto;
  }

  return `000000${texto}`.slice(-6);
};

const formaDePago = (recibo) => {
  const partes = [];

  if (recibo.pago_tipo === 'card') {
    partes.push('Tarjeta');
  } else if (recibo.pago_tipo === 'us_bank_account') {
    partes.push('Cuenta bancaria');
  } else if (recibo.pago_tipo) {
    partes.push(recibo.pago_tipo);
  }

  if (recibo.pago_marca) {
    partes.push(recibo.pago_marca);
  }

  if (recibo.pago_banco) {
    partes.push(recibo.pago_banco);
  }

  if (recibo.pago_ultimos4) {
    partes.push(`**** ${recibo.pago_ultimos4}`);
  }

  return partes.length > 0 ? partes.join(' · ') : 'No informada';
};

const AgendaReceiptPage = ({ match }) => {
  const history = useHistory();
  const location = useLocation();
  const citaId = match && match.params ? match.params.citaId : '';
  const sessionUser = getUserSession();
  const sessionUserId = sessionUser ? sessionUser.id : null;
  const [recibo, setRecibo] = useState(null);
  const [error, setError] = useState('');
  const regresoGuardado = location.state || {};

  const volverAlCalendario = (datos) => {
    const guardado = regresoGuardado.booking || {};
    const presencial = (datos && datos.modalidad === 'presencial')
      || (guardado.modality && guardado.modality.id === 'presencial');
    const especialistaId = Number(
      (datos && datos.especialista_id) || (guardado.specialist && guardado.specialist.id) || 0
    );
    const documento = (datos && datos.terapia_documento)
      || (guardado.specialist && guardado.specialist.cedula)
      || '';

    if ((presencial && !documento) || (!presencial && !especialistaId)) {
      history.push('/agenda');
      return;
    }

    history.push({
      pathname: '/agenda/calendario',
      state: {
        ...guardado,
        modality: presencial
          ? { id: 'presencial', label: 'Presencial' }
          : (guardado.modality || { id: 'online', label: 'Terapia Online' }),
        specialist: presencial
          ? {
              ...(guardado.specialist || {}),
              cedula: documento,
              sede: (datos && datos.sede) || (guardado.specialist && guardado.specialist.sede) || '',
              label: (datos && datos.especialista_nombre) || (guardado.specialist && guardado.specialist.label) || '',
            }
          : {
              ...(guardado.specialist || {}),
              id: especialistaId,
              label: (datos && datos.especialista_nombre) || (guardado.specialist && guardado.specialist.label) || '',
            },
        slotPagado: regresoGuardado.slotPagado || (datos
          ? { fecha: datos.fecha, hora: String(datos.hora || '').slice(0, 5) }
          : undefined),
        pagoStripe: true,
      },
    });
  };

  useEffect(() => {
    if (!sessionUserId || !citaId) {
      setError('No se pudo abrir el recibo.');
      return undefined;
    }

    let vigente = true;

    fetchAgendaReceipt({
      solicitante_id: sessionUserId,
      cita_id: Number(citaId),
    })
      .then((data) => {
        if (vigente) {
          setRecibo(data);
        }
      })
      .catch((err) => {
        if (vigente) {
          setError(err.message || 'No se pudo abrir el recibo.');
        }
      });

    return () => {
      vigente = false;
    };
  }, [citaId, sessionUserId]);

  if (error) {
    return (
      <div className="agenda-root agenda-root--booking">
        <section className="agenda-booking">
          <div className="agenda-payment-card">
            <h1>Recibo</h1>
            <p>{error}</p>
            <button type="button" className="agenda-calendar-back" onClick={() => volverAlCalendario(null)}>
              ← Volver a la agenda
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (!recibo) {
    return (
      <div className="agenda-root agenda-root--booking">
        <section className="agenda-booking">
          <div className="agenda-payment-card">
            <h1>Recibo</h1>
            <p>Cargando recibo…</p>
          </div>
        </section>
      </div>
    );
  }

  const precio = formatAgendaPrecio(recibo.precio, recibo.moneda);

  return (
    <div className="agenda-root agenda-root--booking">
      <section className="agenda-booking">
        <article className="agenda-receipt">
          <p className="agenda-receipt-kicker">Beyond</p>
          <h1>Recibo de pago</h1>
          <p className="agenda-receipt-number">N.º {numeroRecibo(recibo.recibo_numero)}</p>

          <dl className="agenda-payment-summary">
            <div>
              <dt>Pagado por</dt>
              <dd>{recibo.pagador_nombre || '—'}</dd>
            </div>
            <div>
              <dt>Paciente</dt>
              <dd>{recibo.paciente_nombre || '—'}</dd>
            </div>
            <div>
              <dt>Servicio</dt>
              <dd>
                {recibo.servicio_nombre || agendaCitaTipoLabel(recibo.tipo_cita)}
                {recibo.servicio_nombre ? ` · ${agendaCitaTipoLabel(recibo.tipo_cita)}` : ''}
              </dd>
            </div>
            <div>
              <dt>Cita</dt>
              <dd>{formatSlotLabel(recibo.fecha, String(recibo.hora || '').slice(0, 5))}</dd>
            </div>
            {recibo.especialista_nombre ? (
              <div>
                <dt>Especialista</dt>
                <dd>{recibo.especialista_nombre}</dd>
              </div>
            ) : null}
            <div>
              <dt>Forma de pago</dt>
              <dd>{formaDePago(recibo)}</dd>
            </div>
            <div>
              <dt>Monto</dt>
              <dd>{precio || '—'}</dd>
            </div>
            {recibo.pagado_en ? (
              <div>
                <dt>Fecha de pago</dt>
                <dd>{recibo.pagado_en}</dd>
              </div>
            ) : null}
          </dl>

          <div className="agenda-receipt-actions">
            <button type="button" className="agenda-slot-pay-btn" onClick={() => window.print()}>
              Imprimir
            </button>
            <button type="button" className="agenda-calendar-back" onClick={() => volverAlCalendario(recibo)}>
              ← Volver a la agenda
            </button>
          </div>
        </article>
      </section>
    </div>
  );
};

export default AgendaReceiptPage;
